import { supabase } from "@/lib/supabase";
import { addDays, startOfDay } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { inicioDeDiaHace } from "@/lib/format";

export interface DiaDeVentas {
  /** ISO corto, YYYY-MM-DD, en la zona horaria de la tienda. */
  dia: string;
  total: number;
  pedidos: number;
}

export interface ProductoVendido {
  nombre: string;
  variante: string | null;
  unidades: number;
  importe: number;
}

export interface VentaReciente {
  id: string;
  numero: string;
  cliente: string | null;
  total: number;
  estado: string;
  entrega: string;
  pago: string;
  cuando: string;
}

/**
 * Catorce dias de ventas.
 *
 * Se agrupa en el navegador y no en PostgreSQL a proposito: son como mucho
 * unos cientos de filas para una tienda de este tamaño, y una vista agregada
 * obligaria a decidir la zona horaria en la base. La tienda de Lima y una de
 * Madrid no parten el dia a la misma hora, y el corte tiene que salir de
 * `organizations.timezone`.
 */
export async function fetchVentasPorDia(
  organizationId: string,
  timeZone: string,
  dias = 14,
): Promise<DiaDeVentas[]> {
  // Restar dias de calendario, no milisegundos: en una zona con horario de
  // verano un dia no siempre mide 24 horas y los cubos se descolocarian.
  const desde = inicioDeDiaHace(dias - 1, timeZone);

  const { data, error } = await supabase
    .from("orders")
    .select("total, created_at")
    .eq("organization_id", organizationId)
    .neq("order_status", "cancelled")
    .gte("created_at", desde.toISOString())
    .order("created_at");

  if (error) throw error;

  // Los dias sin ventas tienen que existir en la serie: si se omiten, el
  // grafico miente juntando el lunes con el jueves.
  const porDia = new Map<string, { total: number; pedidos: number }>();
  for (let i = 0; i < dias; i++) {
    porDia.set(claveDeDia(addDays(desde, i), timeZone), { total: 0, pedidos: 0 });
  }

  for (const o of data ?? []) {
    const clave = claveDeDia(new Date(o.created_at), timeZone);
    const acc = porDia.get(clave);
    if (!acc) continue;
    acc.total += Number(o.total);
    acc.pedidos += 1;
  }

  return [...porDia].map(([dia, v]) => ({ dia, ...v }));
}

function claveDeDia(fecha: Date, timeZone: string): string {
  // `en-CA` da YYYY-MM-DD, que ordena bien como texto.
  return fecha.toLocaleDateString("en-CA", { timeZone });
}

/**
 * Lo mas vendido de los ultimos treinta dias.
 *
 * Se agrupa por la instantanea guardada en la linea del pedido, no por el
 * producto: si alguien renombra "Ricocan 15kg" a "Ricocan Adultos 15kg", lo
 * vendido antes se vendio con el nombre viejo y asi es como hay que contarlo.
 */
export async function fetchMasVendidos(
  organizationId: string,
  limite = 5,
): Promise<ProductoVendido[]> {
  const desde = startOfDay(new TZDate(Date.now(), "UTC")).toISOString();

  const { data: pedidos, error: errorPedidos } = await supabase
    .from("orders")
    .select("id")
    .eq("organization_id", organizationId)
    .neq("order_status", "cancelled")
    .gte("created_at", desde);

  if (errorPedidos) throw errorPedidos;

  const ids = (pedidos ?? []).map((o) => o.id);
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from("order_items")
    .select("product_name_snapshot, variant_name_snapshot, quantity, total")
    .eq("organization_id", organizationId)
    .in("order_id", ids);

  if (error) throw error;

  const acumulado = new Map<string, ProductoVendido>();
  for (const linea of data ?? []) {
    const nombre = linea.product_name_snapshot ?? "Sin nombre";
    const variante = linea.variant_name_snapshot;
    const clave = `${nombre}::${variante ?? ""}`;

    const previo = acumulado.get(clave) ?? { nombre, variante, unidades: 0, importe: 0 };
    previo.unidades += Number(linea.quantity);
    previo.importe += Number(linea.total);
    acumulado.set(clave, previo);
  }

  return [...acumulado.values()]
    .sort((a, b) => b.unidades - a.unidades)
    .slice(0, limite);
}

export async function fetchUltimasVentas(
  organizationId: string,
  limite = 5,
): Promise<VentaReciente[]> {
  const { data, error } = await supabase
    .from("orders")
    .select("id, order_number, customer_name_snapshot, total, order_status, fulfillment_type, payment_status, created_at")
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false })
    .limit(limite);

  if (error) throw error;

  return (data ?? []).map((o) => ({
    id: o.id,
    numero: o.order_number,
    cliente: o.customer_name_snapshot,
    total: Number(o.total),
    estado: o.order_status,
    entrega: o.fulfillment_type,
    pago: o.payment_status,
    cuando: o.created_at,
  }));
}
